/* Dr. Förster · Warenkorb-Conversion (DRF Cart 1.0)
 * Renders into the Pipeline cart drawer, driven by #drf-cart-config (sections/cart-drawer.liquid):
 *  - free-shipping progress (threshold from the section) and the discount step (e.g. 20 % from 150 €)
 *  - "Make it a routine": swaps single products for the matching bundle (+ gift)
 *  - one-click pairings ("Passt dazu")
 *  - savings line above the checkout button
 * Re-renders on every theme:cart:change and after its own actions.
 */
(() => {
  const el = document.getElementById('drf-cart-config');
  if (!el || window.DRFCart) return;
  let cfg;
  try { cfg = JSON.parse(el.textContent); } catch (e) { return; }
  const L = cfg.labels || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const eligible = new Set((cfg.eligible || []).map(Number));
  let busy = false;
  let last = null;

  const $ = (sel) => document.querySelector(sel);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // fixed = always two decimals (e.g. "23,00 €" in the free-shipping line)
  const money = (cents, fixed) => {
    const lang = document.documentElement.lang || 'de';
    const cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    const whole = cents % 100 === 0 && !fixed;
    try { return new Intl.NumberFormat(lang, { style: 'currency', currency: cur, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100); }
    catch (e) { return `${(cents / 100).toFixed(2)} €`; }
  };
  const rate = () => parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
  const fill = (str, map) => String(str || '').replace(/\[(\w+)\]|\{(\w+)\}/g, (m, a, b) => (map[a || b] != null ? map[a || b] : m));
  const isGift = (i) => i.properties && i.properties._drf_gift;
  const img = (src, w) => (src ? String(src).replace(/width=\d+/, `width=${w}`) : '');

  async function api(path, body) {
    const res = await fetch(root + path, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin',
    });
    if (!res.ok) throw new Error(`${path} ${res.status}`);
    return res.json();
  }

  async function refresh() {
    const cart = await api('cart.js');
    document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart }, bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    render(cart);
    return cart;
  }

  /* ---------- model ---------- */
  function analyse(cart) {
    const items = (cart.items || []).filter((i) => !isGift(i));
    const inCart = new Set(items.map((i) => Number(i.product_id)));
    const threshold = Math.round((cfg.threshold || 0) * rate());
    const base = items.filter((i) => eligible.has(Number(i.product_id))).reduce((n, i) => n + i.final_line_price, 0);

    // Routine upgrade: the bundle whose parts are most present (ties keep editor order).
    let upgrade = null;
    (cfg.upgrades || []).forEach((u) => {
      if (inCart.has(Number(u.bundle.product))) return;
      const present = u.parts.filter((p) => inCart.has(Number(p.product)));
      if (!present.length) return;
      if (!upgrade || present.length > upgrade.present.length) upgrade = { ...u, present };
    });

    // Pairings: max 2, skip ones already covered by the upgrade card.
    const covered = new Set(upgrade ? [Number(upgrade.bundle.product), ...upgrade.parts.map((p) => Number(p.product))] : []);
    const seen = new Set();
    const pairs = [];
    (cfg.pairs || []).forEach((p) => {
      const sid = Number(p.suggest.product);
      if (pairs.length >= 2 || !inCart.has(Number(p.trigger)) || inCart.has(sid) || seen.has(sid)) return;
      if (upgrade && (covered.has(Number(p.trigger)) || covered.has(sid))) return;
      seen.add(sid);
      pairs.push(p);
    });
    const applied = items.some((i) => eligible.has(Number(i.product_id)) && i.final_line_price < i.original_line_price);
    return { items, threshold, base, upgrade, pairs, applied };
  }

  /* ---------- render ---------- */
  function render(cart) {
    last = cart;
    const top = $('[data-drf-cart-top]');
    const boost = $('[data-drf-cart-boost]');
    const savings = $('[data-drf-cart-savings]');
    if (!top && !boost) return;
    const empty = !cart.item_count;
    const m = analyse(cart);

    // Progress: free shipping first, then the discount step
    if (top) {
      const shipT = Math.round((cfg.shipThreshold || 0) * rate());
      const shipBase = cart.total_price || 0;
      const shipDone = shipT > 0 && shipBase >= shipT;
      const offerActive = m.threshold && eligible.size && m.base > 0;
      const offerDone = offerActive && m.base >= m.threshold;
      const offerText = m.applied ? (L.offerApplied || L.offerReached) : L.offerReached;
      let goal = null;
      if (shipT > 0 && !shipDone) goal = { pct: shipBase / shipT, text: fill(esc(L.shipGap), { amount: `<strong>${money(shipT - shipBase, true)}</strong>` }), done: false };
      else if (offerActive && !offerDone) goal = { pct: m.base / m.threshold, text: fill(esc(L.offerGap), { amount: `<strong>${money(m.threshold - m.base)}</strong>` }), done: false };
      else if (offerDone) goal = { pct: 1, text: esc(offerText), done: true };
      else if (shipDone) goal = { pct: 1, text: esc(L.shipReached), done: true };
      if (empty || !goal) {
        top.innerHTML = '';
        top.hidden = true;
      } else {
        const pct = Math.min(100, Math.round(goal.pct * 100));
        const check = '<span class="drf-cart-progress__check" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg></span>';
        const chips = [];
        if (shipDone && !(goal.done && !offerActive)) chips.push(esc(L.shipReached));
        if (offerDone && goal.text !== esc(offerText)) chips.push(esc(offerText));
        top.hidden = false;
        top.innerHTML = `
          <div class="drf-cart-progress${goal.done ? ' is-reached' : ''}">
            <p class="drf-cart-progress__text">${goal.done ? check : ''}<span>${goal.text}</span></p>
            ${goal.done ? '' : `<div class="drf-cart-progress__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}"><span style="--p:${pct}%"></span></div>`}
            ${chips.length ? `<ul class="drf-cart-progress__done">${chips.map((c) => `<li>${check}${c}</li>`).join('')}</ul>` : ''}
          </div>`;
        requestAnimationFrame(() => top.querySelector('.drf-cart-progress')?.classList.add('is-drawn'));
      }
    }

    // Upgrade + pairings
    if (boost) {
      let html = '';
      if (!empty && m.upgrade) {
        const u = m.upgrade;
        const presentTotal = u.present.reduce((n, p) => n + p.price, 0);
        const extra = Math.max(0, u.bundle.price - presentTotal);
        const missing = u.parts.filter((p) => !u.present.includes(p)).map((p) => p.title).join(' + ');
        html += `
          <div class="drf-cart-upgrade">
            <p class="drf-cart-eyebrow">${esc(L.upgradeEyebrow)}</p>
            <div class="drf-cart-upgrade__row">
              <span class="drf-cart-upgrade__img">${u.bundle.image ? `<img src="${esc(img(u.bundle.image, 240))}" alt="" width="120" height="120" loading="lazy">` : ''}<span class="drf-cart-upgrade__badge" aria-hidden="true">+1</span></span>
              <div class="drf-cart-upgrade__copy">
                <p class="drf-cart-upgrade__title">${esc(u.title || u.bundle.title)}</p>
                ${missing ? (u.text ? `<p class="drf-cart-upgrade__text">${fill(esc(u.text), { missing: esc(missing) })}</p>` : '') : (L.upgradeSame ? `<p class="drf-cart-upgrade__text">${esc(L.upgradeSame)}</p>` : '')}
                <p class="drf-cart-upgrade__value">${cfg.giftMax ? `<span>${fill(esc(L.upgradeValue), { amount: money(Math.round(cfg.giftMax * rate())) })}</span>` : ''}${u.present.length < u.parts.length ? `<strong>${fill(esc(L.upgradeExtra), { amount: money(extra) })}</strong>` : ''}</p>
              </div>
            </div>
            <button type="button" class="drf-cart-upgrade__cta" data-drf-cart-upgrade>${esc(L.upgradeCta)}</button>
          </div>`;
      }
      if (!empty && m.pairs.length) {
        html += `<div class="drf-cart-pairs"><p class="drf-cart-eyebrow">${esc(L.pairEyebrow)}</p><ul>`;
        m.pairs.forEach((p) => {
          const s = p.suggest;
          // The step discount applies to an additional product once the rest of the cart reaches the threshold.
          const discounted = m.threshold && eligible.has(Number(s.product)) && m.base >= m.threshold && !m.applied;
          html += `
            <li class="drf-cart-card">
              <a class="drf-cart-card__img" href="${esc(s.url)}">${s.image ? `<img src="${esc(img(s.image, 200))}" alt="" width="100" height="100" loading="lazy">` : ''}</a>
              <div class="drf-cart-card__body">
                <a class="drf-cart-card__title" href="${esc(s.url)}">${esc(s.title)}</a>
                ${p.reason ? `<span class="drf-cart-card__reason">${esc(p.reason)}</span>` : ''}
                <span class="drf-cart-card__price">${discounted ? `<s>${money(s.price)}</s>${money(Math.round(s.price * (1 - (cfg.offerPercent || 0) / 100)))}` : money(s.price)}${discounted && L.offerUnlock ? `<em>${esc(L.offerUnlock)}</em>` : ''}</span>
              </div>
              <button type="button" class="drf-cart-card__add" data-drf-cart-add="${s.variant}" aria-label="${esc(L.pairCta)}: ${esc(s.title)}">${esc(L.pairCta)}</button>
            </li>`;
        });
        html += '</ul></div>';
      }
      html += `<p class="drf-cart-error" role="alert" hidden>${esc(L.error)}</p>`;
      boost.innerHTML = html;
    }

    // Savings above the checkout button
    if (savings) {
      const saved = (cart.items || []).reduce((n, i) => n + (i.original_line_price - i.final_line_price), 0) + (cart.cart_level_discount_applications || []).reduce((n, d) => n + (d.total_allocated_amount || 0), 0);
      if (!empty && saved > 0 && L.savings) {
        savings.hidden = false;
        savings.innerHTML = `<span class="drf-cart-savings__tag" aria-hidden="true">%</span>${fill(esc(L.savings), { amount: `<strong>${money(saved)}</strong>` })}`;
      } else {
        savings.hidden = true;
        savings.innerHTML = '';
      }
    }
  }

  function showError() {
    const e = document.querySelector('.drf-cart-error');
    if (e) e.hidden = false;
  }

  /* ---------- actions ---------- */
  async function doUpgrade(button) {
    if (busy || !last) return;
    const m = analyse(last);
    const u = m.upgrade;
    if (!u) return;
    busy = true;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      // Take one of each present part out, then add the bundle (the gift pop-up reacts to the add).
      for (const part of u.present) {
        const line = m.items.find((i) => Number(i.product_id) === Number(part.product));
        if (line) await api('cart/change.js', { id: line.key, quantity: line.quantity - 1 });
      }
      await api('cart/add.js', { items: [{ id: u.bundle.variant, quantity: 1 }] });
      await refresh();
    } catch (e) {
      showError();
      button.disabled = false;
      button.classList.remove('is-loading');
    } finally {
      busy = false;
    }
  }

  async function doAdd(button) {
    if (busy) return;
    busy = true;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      await api('cart/add.js', { items: [{ id: Number(button.dataset.drfCartAdd), quantity: 1 }] });
      await refresh();
      const drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (drawer && !drawer.classList.contains('drawer--visible')) {
        const toggle = document.querySelector('[data-drawer-toggle="drawer-cart"]');
        if (toggle) toggle.click();
      }
    } catch (e) {
      showError();
      button.disabled = false;
      button.classList.remove('is-loading');
    } finally {
      busy = false;
    }
  }

  document.addEventListener('click', (e) => {
    const up = e.target.closest && e.target.closest('[data-drf-cart-upgrade]');
    if (up) { e.preventDefault(); doUpgrade(up); return; }
    const add = e.target.closest && e.target.closest('[data-drf-cart-add]');
    if (add) { e.preventDefault(); doAdd(add); }
  });

  document.addEventListener('theme:cart:change', (e) => {
    const cart = e.detail && e.detail.cart;
    if (cart && cart.items) render(cart);
    else api('cart.js').then(render).catch(() => {});
  });

  // Cart page: the theme swaps line items / totals via AJAX; re-read the cart when that happens.
  let pageTimer = null;
  const schedule = () => {
    clearTimeout(pageTimer);
    pageTimer = setTimeout(() => { if (!busy) api('cart.js').then(render).catch(() => {}); }, 300);
  };
  document.querySelectorAll('.cart__template [data-line-items], .cart__template [data-cart-final]').forEach((node) => {
    new MutationObserver(schedule).observe(node, { childList: true, subtree: true, characterData: true });
  });

  window.DRFCart = { render: () => api('cart.js').then(render), config: cfg };
  api('cart.js').then(render).catch(() => {});
})();
